import React, { useEffect, useState } from "react";
import { createStrategy, getExpiries, getUnderlyings } from "../api";
import ExitTimePicker from "./ExitTimePicker";

// ₹5-wide preset buckets from 0 up to 100, plus one open-ended "100+" bucket.
// At entry, the backend picks whichever strike's live LTP falls nearest to
// [min, max] (max=null means no upper bound) - see instruments.py.
const PRESET_BUCKETS = [
  ...Array.from({ length: 20 }, (_, i) => {
    const min = i * 5;
    const max = min + 5;
    return { key: `${min}-${max}`, label: `₹${min} - ₹${max}`, min, max };
  }),
  { key: "100+", label: "₹100+", min: 100, max: null },
];

const BLANK_LEG = {
  transaction_type: "BUY",
  option_type: "CE",
  range_mode: "preset",     // "preset" | "custom"
  preset: PRESET_BUCKETS[0].key,
  custom_min: "",
  custom_max: "",
};

// Resolves a leg's UI state down to the {premium_min, premium_max} pair the
// backend expects. Returns null if a custom range is incomplete/invalid.
function resolveLegRange(leg) {
  if (leg.range_mode === "preset") {
    const bucket = PRESET_BUCKETS.find((b) => b.key === leg.preset) || PRESET_BUCKETS[0];
    return { premium_min: bucket.min, premium_max: bucket.max };
  }
  const min = Number(leg.custom_min);
  if (leg.custom_min === "" || Number.isNaN(min) || min < 0) return null;
  if (leg.custom_max === "" || leg.custom_max === null) {
    return { premium_min: min, premium_max: null };
  }
  const max = Number(leg.custom_max);
  if (Number.isNaN(max) || max <= min) return null;
  return { premium_min: min, premium_max: max };
}

const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const DEFAULT_FORM = {
  name: "",
  underlying: "",
  expiry_rule: "nearest_weekly", // nearest_weekly (auto, reusable every week) | fixed (pick one literal date)
  expiry: "",
  price_basis: "spot",
  lots: 1,
  product: "MIS",
  entry_mode: "manual",
  entry_window_start: "09:20",
  entry_window_end: "15:15",
  entry_days: ["mon", "tue", "wed", "thu", "fri"],
  square_off_time: "",
  target_type: "amount",
  target_value: "",
  stoploss_type: "amount",
  stoploss_value: "",
  stoploss_mode: "fixed",
  lock_trigger: "",
  lock_amount: "",
  trail_trigger: "",
  trail_by: "",
  move_sl_to_cost: false,
  move_sl_to_cost_trigger: "",
};

export default function StrategyBuilder({ onCreated }) {
  const [underlyings, setUnderlyings] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [expiryError, setExpiryError] = useState(null);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [legs, setLegs] = useState([{ ...BLANK_LEG }, { ...BLANK_LEG, transaction_type: "SELL" }]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getUnderlyings()
      .then((res) => setUnderlyings(res.data.underlyings || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Only need the literal expiry list when the user might pick one by
    // hand - in "nearest_weekly" mode nothing is ever shown, so skip the
    // API call entirely.
    if (!form.underlying || form.expiry_rule !== "fixed") {
      setExpiries([]);
      return;
    }
    setExpiryError(null);
    getExpiries(form.underlying)
      .then((res) => setExpiries(res.data.expiries || []))
      .catch((err) => {
        setExpiries([]);
        setExpiryError(err.response?.data?.detail || err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.underlying, form.expiry_rule]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setLeg = (index, key) => (e) => {
    const value = e.target.value;
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, [key]: value } : leg)));
  };

  const setLegField = (index, key, value) => {
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, [key]: value } : leg)));
  };

  const setExitTime = (value) => setForm((f) => ({ ...f, square_off_time: value }));

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      entry_days: f.entry_days.includes(day)
        ? f.entry_days.filter((d) => d !== day)
        : [...f.entry_days, day],
    }));
  };

  const addLeg = () => setLegs((prev) => [...prev, { ...BLANK_LEG }]);
  const removeLeg = (index) => setLegs((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name || !form.underlying) {
      setError("Name and underlying are required.");
      return;
    }
    if (form.expiry_rule === "fixed" && !form.expiry) {
      setError("Pick a fixed expiry date, or switch to Auto (nearest weekly) to skip this.");
      return;
    }
    if (legs.length === 0) {
      setError("Add at least one leg.");
      return;
    }
    const resolvedRanges = legs.map(resolveLegRange);
    for (let i = 0; i < resolvedRanges.length; i++) {
      if (!resolvedRanges[i]) {
        setError(`Leg ${i + 1}: enter a valid premium range (e.g. min 3, max 5).`);
        return;
      }
    }
    if (form.entry_mode !== "manual" && !form.entry_window_start) {
      setError("Entry window start time is required for scheduled entry.");
      return;
    }
    if (form.entry_mode !== "manual" && form.entry_days.length === 0) {
      setError("Pick at least one day for scheduled entry, or it will never fire.");
      return;
    }

    setBusy(true);
    try {
      await createStrategy({
        name: form.name,
        underlying: form.underlying,
        expiry_rule: form.expiry_rule,
        expiry: form.expiry_rule === "fixed" ? form.expiry : "",
        price_basis: form.price_basis,
        lots: Number(form.lots) || 1,
        legs: legs.map((l, i) => ({
          transaction_type: l.transaction_type,
          option_type: l.option_type,
          premium_min: resolvedRanges[i].premium_min,
          premium_max: resolvedRanges[i].premium_max,
        })),
        product: form.product,
        entry_mode: form.entry_mode,
        entry_window_start: form.entry_window_start || null,
        entry_window_end: form.entry_window_end || null,
        entry_days: form.entry_days,
        square_off_time: form.square_off_time || null,
        target_type: form.target_type,
        target_value: Number(form.target_value) || 0,
        stoploss_type: form.stoploss_type,
        stoploss_value: Number(form.stoploss_value) || 0,
        stoploss_mode: form.stoploss_mode,
        lock_trigger: Number(form.lock_trigger) || 0,
        lock_amount: Number(form.lock_amount) || 0,
        trail_trigger: Number(form.trail_trigger) || 0,
        trail_by: Number(form.trail_by) || 0,
        move_sl_to_cost: !!form.move_sl_to_cost,
        move_sl_to_cost_trigger: Number(form.move_sl_to_cost_trigger) || 0,
      });
      setForm(DEFAULT_FORM);
      setLegs([{ ...BLANK_LEG }, { ...BLANK_LEG, transaction_type: "SELL" }]);
      setExpiries([]);
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="strategy-builder" onSubmit={handleSubmit}>
      <div className="field-group">
        <label>Strategy name</label>
        <input type="text" value={form.name} onChange={set("name")} placeholder="e.g. Nifty weekly IC" />
      </div>

      <div className="field-group inline">
        <div>
          <label>Underlying</label>
          <select value={form.underlying} onChange={set("underlying")}>
            <option value="">Select...</option>
            {underlyings.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Expiry</label>
          <div className="segmented">
            <span
              className={`segmented-option ${form.expiry_rule === "nearest_weekly" ? "active" : ""}`}
              onClick={() => setForm((f) => ({ ...f, expiry_rule: "nearest_weekly" }))}
            >
              Auto (nearest weekly)
            </span>
            <span
              className={`segmented-option ${form.expiry_rule === "fixed" ? "active" : ""}`}
              onClick={() => setForm((f) => ({ ...f, expiry_rule: "fixed" }))}
            >
              Fixed date
            </span>
          </div>
          {form.expiry_rule === "nearest_weekly" ? (
            <p className="hint">
              Resolved fresh whenever you click Enter Now - whichever expiry is soonest that day.
              Save this once, reuse it every week without editing.
            </p>
          ) : (
            <>
              <select value={form.expiry} onChange={set("expiry")} disabled={!form.underlying} style={{ marginTop: 8 }}>
                <option value="">Select...</option>
                {expiries.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
              {expiryError && <p className="field-error">{expiryError}</p>}
              <p className="hint">
                Locked to this exact date - once it passes, this strategy will need a new expiry before it can enter again.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="field-group">
        <label>Underlying basis</label>
        <div className="segmented">
          <span className="segmented-option active">Spot</span>
          <span className="segmented-option disabled" title="Not implemented yet">Futures</span>
          <span className="segmented-option disabled" title="Not implemented yet">Synthetic Futures</span>
        </div>
      </div>

      <div className="field-group inline">
        <div>
          <label>Lots (multiplier)</label>
          <input type="number" min="1" value={form.lots} onChange={set("lots")} />
        </div>
        <div>
          <label>Trade type</label>
          <div className="segmented">
            <span
              className={`segmented-option ${form.product === "MIS" ? "active" : ""}`}
              onClick={() => setForm((f) => ({ ...f, product: "MIS" }))}
            >
              Intraday
            </span>
            <span
              className={`segmented-option ${form.product === "NRML" ? "active" : ""}`}
              onClick={() => setForm((f) => ({ ...f, product: "NRML" }))}
            >
              Positional
            </span>
          </div>
        </div>
      </div>

      <div className="field-group">
        <label>Legs</label>
        {legs.map((leg, i) => (
          <div key={i} className="leg-card">
            <div className="leg-row-top">
              <select value={leg.transaction_type} onChange={setLeg(i, "transaction_type")}>
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
              <select value={leg.option_type} onChange={setLeg(i, "option_type")}>
                <option value="CE">CE</option>
                <option value="PE">PE</option>
              </select>
              <button type="button" className="leg-remove" onClick={() => removeLeg(i)} disabled={legs.length <= 1}>
                &times;
              </button>
            </div>
            <div className="leg-row-range">
              <div className="segmented small">
                <span
                  className={`segmented-option ${leg.range_mode === "preset" ? "active" : ""}`}
                  onClick={() => setLegField(i, "range_mode", "preset")}
                >
                  Preset
                </span>
                <span
                  className={`segmented-option ${leg.range_mode === "custom" ? "active" : ""}`}
                  onClick={() => setLegField(i, "range_mode", "custom")}
                >
                  Manual
                </span>
              </div>
              {leg.range_mode === "preset" ? (
                <select value={leg.preset} onChange={setLeg(i, "preset")}>
                  {PRESET_BUCKETS.map((b) => (
                    <option key={b.key} value={b.key}>{b.label}</option>
                  ))}
                </select>
              ) : (
                <div className="leg-custom-range">
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    placeholder="Min ₹"
                    value={leg.custom_min}
                    onChange={setLeg(i, "custom_min")}
                  />
                  <span className="range-sep">–</span>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    placeholder="Max ₹ (blank = open-ended)"
                    value={leg.custom_max}
                    onChange={setLeg(i, "custom_max")}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        <button type="button" onClick={addLeg} className="add-leg-btn">Add Leg</button>
        <p className="hint">
          Each leg's strike is picked at entry: the OTM call/put whose live premium falls nearest to
          that leg's range (inside the range counts as a perfect match). A classic iron condor is
          BUY CE (cheap, far OTM), BUY PE (cheap, far OTM), SELL CE (near OTM), SELL PE (near OTM) -
          a lower premium range is inherently further OTM.
        </p>
      </div>

      <div className="field-group inline">
        <div>
          <label>Entry mode</label>
          <select value={form.entry_mode} onChange={set("entry_mode")}>
            <option value="manual">Manual button only</option>
            <option value="scheduled">Scheduled window only</option>
            <option value="both">Manual button + scheduled window</option>
          </select>
        </div>
      </div>

      <div className="field-group inline">
        <div>
          <label>Start time (IST)</label>
          <input type="time" value={form.entry_window_start} onChange={set("entry_window_start")} />
        </div>
        <div>
          <label>End time (IST)</label>
          <input type="time" value={form.entry_window_end} onChange={set("entry_window_end")} />
        </div>
      </div>
      <p className="hint" style={{ marginTop: -8 }}>
        Scheduled entry fires once start time is reached (while still idle). For Intraday trade
        type, End time also auto-squares-off this strategy if it's still open.
      </p>

      {form.entry_mode !== "manual" && (
        <div className="field-group">
          <label>Days scheduled entry is allowed on</label>
          <div className="weekday-picker">
            {WEEKDAYS.map((d) => (
              <span
                key={d.key}
                className={`weekday-pill ${form.entry_days.includes(d.key) ? "active" : ""}`}
                onClick={() => toggleDay(d.key)}
              >
                {d.label}
              </span>
            ))}
          </div>
          <p className="hint">
            The manual Enter Now button always works regardless of this. Only affects the
            scheduled window above.
          </p>
        </div>
      )}

      <p className="section-label">MTM target</p>
      <div className="field-group inline">
        <div>
          <label>Type</label>
          <select value={form.target_type} onChange={set("target_type")}>
            <option value="amount">Amount (₹)</option>
            <option value="percent">% of premium collected</option>
          </select>
        </div>
        <div>
          <label>Value</label>
          <input type="number" value={form.target_value} onChange={set("target_value")} placeholder="e.g. 3000" />
        </div>
      </div>
      <p className="hint" style={{ marginTop: -8 }}>Action on hit: square off all legs.</p>

      <p className="section-label">MTM stoploss</p>
      <div className="field-group inline">
        <div>
          <label>Type</label>
          <select value={form.stoploss_type} onChange={set("stoploss_type")}>
            <option value="amount">Amount (₹, negative)</option>
            <option value="percent">% of premium collected</option>
          </select>
        </div>
        <div>
          <label>Value</label>
          <input
            type="number"
            value={form.stoploss_value}
            onChange={set("stoploss_value")}
            placeholder={form.stoploss_type === "amount" ? "e.g. -2000" : "e.g. 40"}
          />
        </div>
      </div>

      <p className="section-label">Exit time (optional)</p>
      <div className="field-group">
        <label>Square off all legs at this time if target/stoploss haven't fired</label>
        <ExitTimePicker value={form.square_off_time} onChange={setExitTime} />
        <p className="hint">
          Applies on top of target/stoploss above, regardless of trade type. Leave blank to only
          exit on target/stoploss.
        </p>
      </div>

      <div className="field-group">
        <label>Profit lock and trailing stoploss</label>
        <select value={form.stoploss_mode} onChange={set("stoploss_mode")}>
          <option value="fixed">None</option>
          <option value="profit_lock">Lock profit</option>
          <option value="trailing">Trailing stoploss</option>
        </select>
      </div>

      {form.stoploss_mode === "profit_lock" && (
        <div className="field-group inline">
          <div>
            <label>Activate once profit reaches (₹)</label>
            <input type="number" value={form.lock_trigger} onChange={set("lock_trigger")} placeholder="e.g. 2000" />
          </div>
          <div>
            <label>Lock in profit of (₹)</label>
            <input type="number" value={form.lock_amount} onChange={set("lock_amount")} placeholder="e.g. 1000" />
          </div>
        </div>
      )}

      {form.stoploss_mode === "trailing" && (
        <div className="field-group inline">
          <div>
            <label>Activate once profit reaches (₹)</label>
            <input type="number" value={form.trail_trigger} onChange={set("trail_trigger")} placeholder="e.g. 2000" />
          </div>
          <div>
            <label>Trail behind peak by (₹)</label>
            <input type="number" value={form.trail_by} onChange={set("trail_by")} placeholder="e.g. 800" />
          </div>
        </div>
      )}

      <div className="field-group">
        <label className="checkbox-label">
          <input type="checkbox" checked={form.move_sl_to_cost} onChange={set("move_sl_to_cost")} />
          Move SL to cost (breakeven) once profit reaches a trigger
        </label>
        {form.move_sl_to_cost && (
          <input
            type="number"
            style={{ marginTop: 8 }}
            value={form.move_sl_to_cost_trigger}
            onChange={set("move_sl_to_cost_trigger")}
            placeholder="Profit trigger (₹), e.g. 1500"
          />
        )}
      </div>

      {error && <div className="banner error">{error}</div>}

      <button type="submit" disabled={busy}>
        {busy ? "Saving..." : "Save Strategy"}
      </button>
    </form>
  );
}
